import { BookmarkCategory } from '@/types/api';
import type { NewsFeedResponse, NewsItem } from '@/types/api';

/**
 * Development-only stand-in for GET /data/news (NewsDataHandler).
 *
 * The real payload is a precomputed ranking blob published to Redis by
 * script/news_rank.py; a local stack with an empty `news:v1:latest` key
 * serves `{generated_at: null, items: []}`, which renders the empty state
 * and leaves the page impossible to work on. Shapes here mirror the
 * handler exactly: no engagement counts, no user attribution, and `saved`
 * only on items an authenticated request would resolve as already saved.
 */

const HOUR = 3600;

/** Self-contained thumbnail so DEV data never depends on the network. */
function ogImage(background: string, foreground: string, label: string): string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="240" height="160">
    <rect width="240" height="160" fill="${background}"/>
    <circle cx="188" cy="26" r="64" fill="${foreground}" opacity="0.35"/>
    <text x="20" y="126" font-family="Georgia, serif" font-size="72"
          font-weight="700" fill="${foreground}">${label}</text>
  </svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg.replace(/\s+/g, ' '))}`;
}

type MockNewsSeed = Omit<NewsItem, 'rank' | 'first_seen'> & {
  /** Hours before "now"; converted to a unix `first_seen` at call time. */
  hoursAgo: number;
};

const MOCK_NEWS_SEEDS: MockNewsSeed[] = [
  {
    url: 'https://www.newyorker.com/magazine/the-quiet-part-of-the-internet',
    title: 'The Quiet Part of the Internet',
    host: 'newyorker.com',
    og_image: ogImage('#1c1917', '#fbbf24', 'NY'),
    category: BookmarkCategory.ARTICLE,
    hoursAgo: 2,
    saved: true,
  },
  {
    url: 'https://www.theatlantic.com/technology/archive/attention-recession/',
    title: 'We Are Living Through an Attention Recession',
    host: 'theatlantic.com',
    og_image: ogImage('#7f1d1d', '#fee2e2', 'TA'),
    category: BookmarkCategory.ARTICLE,
    hoursAgo: 3,
  },
  {
    url: 'https://arstechnica.com/science/2026/08/the-telescope-that-refuses-to-die/',
    title: 'The Telescope That Refuses to Die',
    host: 'arstechnica.com',
    og_image: ogImage('#0c4a6e', '#e0f2fe', 'AT'),
    category: BookmarkCategory.ARTICLE,
    hoursAgo: 5,
  },
  {
    // No og_image: exercises the placeholder tile in NewsThumbnail.
    url: 'https://danluu.com/why-your-build-is-slow/',
    title: 'Why Your Build Is Slow (And Why Nobody Fixes It)',
    host: 'danluu.com',
    og_image: null,
    category: BookmarkCategory.ARTICLE,
    hoursAgo: 6,
  },
  {
    url: 'https://www.nytimes.com/2026/08/19/business/the-last-newsstand.html',
    title: 'The Last Newsstand in Midtown',
    host: 'nytimes.com',
    og_image: ogImage('#171717', '#f5f5f4', 'NT'),
    category: BookmarkCategory.ARTICLE,
    hoursAgo: 8,
    saved: true,
  },
  {
    url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
    title: 'How a Mechanical Watch Works — In One Take',
    host: 'youtube.com',
    og_image: ogImage('#450a0a', '#fecaca', 'YT'),
    category: BookmarkCategory.VIDEO,
    hoursAgo: 9,
  },
  {
    url: 'https://longreads.com/2026/08/18/the-cartographer-of-forgotten-roads/',
    title: 'The Cartographer of Forgotten Roads',
    host: 'longreads.com',
    og_image: ogImage('#134e4a', '#ccfbf1', 'LR'),
    category: BookmarkCategory.ARTICLE,
    hoursAgo: 11,
  },
  {
    // A title the ranker never resolved: the page falls back to the URL.
    url: 'https://simonwillison.net/2026/Aug/17/small-models-big-jobs/',
    title: null,
    host: 'simonwillison.net',
    og_image: null,
    category: BookmarkCategory.ARTICLE,
    hoursAgo: 13,
  },
  {
    url: 'https://www.wired.com/story/the-battery-that-ate-the-grid/',
    title: 'The Battery That Ate the Grid',
    host: 'wired.com',
    og_image: ogImage('#0f172a', '#a5b4fc', 'WI'),
    category: BookmarkCategory.ARTICLE,
    hoursAgo: 16,
  },
  {
    url: 'https://www.theverge.com/2026/8/17/handheld-computing-is-back',
    title: 'Handheld Computing Is Back, and It Is Weird Again',
    host: 'theverge.com',
    og_image: ogImage('#4c1d95', '#ede9fe', 'TV'),
    category: BookmarkCategory.ARTICLE,
    hoursAgo: 19,
  },
  {
    url: 'https://research.example.edu/papers/2026/reading-retention.pdf',
    title: 'Reading Retention in Long-Form Digital Media (PDF)',
    host: 'research.example.edu',
    og_image: null,
    category: BookmarkCategory.PDF,
    hoursAgo: 22,
  },
  {
    url: 'https://www.bloomberg.com/news/features/2026-08-16/the-coffee-cartel',
    title: 'Inside the Coffee Cartel Nobody Admits Exists',
    host: 'bloomberg.com',
    og_image: ogImage('#1e293b', '#fdba74', 'BB'),
    category: BookmarkCategory.ARTICLE,
    hoursAgo: 27,
  },
  {
    url: 'https://aeon.co/essays/what-boredom-is-for',
    title: 'What Boredom Is For',
    host: 'aeon.co',
    og_image: ogImage('#3f3f46', '#fafafa', 'AE'),
    category: BookmarkCategory.ARTICLE,
    hoursAgo: 34,
  },
  {
    url: 'https://www.propublica.org/article/the-permit-that-never-came',
    title: 'The Permit That Never Came',
    host: 'propublica.org',
    og_image: ogImage('#7c2d12', '#ffedd5', 'PP'),
    category: BookmarkCategory.ARTICLE,
    hoursAgo: 41,
  },
  {
    url: 'https://www.economist.com/culture/2026/08/15/the-second-life-of-the-paperback',
    title: 'The Second Life of the Paperback',
    host: 'economist.com',
    og_image: ogImage('#881337', '#ffe4e6', 'EC'),
    category: BookmarkCategory.ARTICLE,
    hoursAgo: 47,
  },
];

/**
 * Build the fake feed. `first_seen` is relative to the current clock so the
 * "3h ago" line stays plausible however long the dev server has been up.
 */
function createMockNewsFeed(limit = 50): NewsFeedResponse {
  const now = Math.floor(Date.now() / 1000);
  const items: NewsItem[] = MOCK_NEWS_SEEDS.slice(0, limit).map(
    ({ hoursAgo, ...item }, index) => ({
      ...item,
      rank: index + 1,
      first_seen: now - hoursAgo * HOUR,
    }),
  );
  return { generated_at: now - 10 * 60, items };
}

export { createMockNewsFeed };
